import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { formatRoute, type PanelRoute } from '../router.ts';
import { phase1Panels } from '../panels-registry.ts';

/**
 * `<exhibition-topbar>` — header strip.
 *
 * Holds the project title, version (rendered statically; the app reads it
 * from package.json at build time in a later phase), a vendor-grouped panel
 * selector, the phase status indicator, theme controls, and an info button
 * that toggles the keyboard-shortcut overlay (key `?`).
 */
@customElement('exhibition-topbar')
export class ExhibitionTopbarElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: var(--space-lg);
        padding-block: var(--space-sm);
        padding-inline: var(--space-lg);
        border-block-end: 1px solid var(--border, #333);
        background: var(--surface, #252525);
      }

      .title {
        font-weight: 600;
        font-size: var(--type-large);
      }

      .selector {
        display: inline-flex;
        gap: var(--space-sm);
        flex-wrap: wrap;
      }

      .selector a {
        font-family: var(--font-data);
        font-size: var(--type-small);
        text-decoration: none;
        color: inherit;
        padding-block: var(--space-xs);
        padding-inline: var(--space-sm);
        border: 1px solid var(--border, #333);
        border-radius: var(--radius-sm, 0.1875rem);
      }

      .selector a[aria-current='page'] {
        background: var(--surface-active, #333);
        border-color: var(--border-strong, #444);
      }

      .help {
        font-family: var(--font-data);
        font-size: var(--type-small);
      }
    `,
  ];

  @property({ attribute: false }) accessor route: PanelRoute | null = null;

  @state() private accessor helpOpen = false;

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('keydown', this.#onKey);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this.#onKey);
  }

  #onKey = (e: KeyboardEvent): void => {
    if (e.key !== '?') return;
    if (isTypingTarget(e.target)) return;
    this.helpOpen = !this.helpOpen;
  };

  override render() {
    const active = this.route;
    return html`
      <span class="title">pedestal-panels</span>
      <nav class="selector" aria-label="Panel catalogue">
        ${phase1Panels.map(
          (p) => html`
            <a
              href=${formatRoute({ vendor: p.vendor, model: p.id })}
              aria-current=${active?.model === p.id ? 'page' : 'false'}
              >${p.productName}</a
            >
          `,
        )}
      </nav>
      <span class="help" aria-live="polite">
        ${this.helpOpen ? 'i: ids · g: grid · r: reference · ?: close' : '? for help'}
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'exhibition-topbar': ExhibitionTopbarElement;
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  return target.isContentEditable;
}
